/**
 * Cross-device (both mouse and touch) hovering and toggling elements without CSS :hover.
 *
 * @author Felix Kiesewetter <info@felixkiesewetter.de>
 * @lastModified 2026-05-30 TypeScript and semicolons.
 * @package fk_js/hover
 * @version 4.0.0
 */

// TODO: Settings Interface.

/**
 * Hover on an element.
 */
HTMLElement.prototype.hover = function(options :object) :void
{

    const touch = 'ontouchstart' in document

    const settings = {
        ...{
            mode: 'hover',
            pre: (element :HTMLElement|Node) :void => {
                element.classList.add(settings.readyClass)
            },
            in: (element :HTMLElement|Node) :void => {
                element.classList.add(settings.class)
            },
            out: (element :HTMLElement|Node) :void => {
                element.classList.remove(settings.class)
            },
            post: (element :HTMLElement|Node) :void => {
                element.classList.remove(settings.readyClass)
            },
            inDelay: 50, // Specifies the duration the "pre" state will consume
            outDelay: options && 'toggle' === options.mode ? 0 : 500,
            postDelay: 500, // Specifies the duration the "post" state will consume
            group: null,
            class: options && 'toggle' === options.mode ? '-toggle' : '-hover',
            readyClass: '-ready',
            requiresUserInteraction: false,
            debug: false
        },
        ...options
    }

    let timeout :ReturnType<typeof setTimeout>;

    settings.debug && console.log('[HOVER]', settings);

    const callback = async (callback :Function, element :HTMLElement, group :string|null = null) :Promise<void> => {
        null === group && clearTimeout(timeout);
        if (callback === settings.in) {
            settings.debug && console.log('[HOVER]', 'PRE');
            settings.pre(element)
            timeout = setTimeout(() => {
                settings.debug && console.log('[HOVER]', 'IN');
                callback(element);
            }, settings.inDelay);
            return;
        }
        timeout = setTimeout(() => {
            settings.debug && console.log('[HOVER]', 'OUT');
            callback(element);
            setTimeout(() => {
                settings.debug && console.log('[HOVER]', 'POST');
                settings.post(element);
            }, settings.postDelay);
        }, settings.outDelay);
    }

    switch (settings.mode) {

        case 'hover':
            if (touch) {
                if (settings.group) {
                    this.dataset.hoverGroup = settings.group;
                }
                this._hover = false;
                this.addEventListener('touchend', (e :TouchEvent) :void => {
                    e.stopPropagation()
                    if (settings.group) {
                        document.querySelectorAll(`[data-hover-group="${settings.group}"]`)?.forEach((element :Node) :void => {
                            if (element === this) {
                                return;
                            }
                            callback(settings.out, element as HTMLElement, settings.group);
                        });
                    }
                    if (!this._hover) {
                        e.preventDefault();
                        this._hover = true;
                    }
                    callback(settings.in, this);
                })
                window.addEventListener('touchend', (e :TouchEvent) :void => {
                    if (e.target?.closest('a[href]')) {
                        return;
                    }
                    if (this._hover) {
                        callback(settings.out, this);
                        this._hover = false;
                    }
                });
                return;
            }
            if (settings.requiresUserInteraction) {
                const initialUserInteraction = (e :TouchEvent|PointerEvent) :void => {
                    if (e.target === this || (this.contains && this.contains(e.target as HTMLElement))) {
                        callback(settings.in, this);
                    }
                    window.removeEventListener('click', initialUserInteraction);
                    this.addEventListener('mouseenter', () :void => {
                        callback(settings.in, this);
                    });
                }
                window.addEventListener('click', initialUserInteraction);
            }
            else {
                this.addEventListener('mouseenter', () :void => {
                    callback(settings.in, this);
                });
            }
            this.addEventListener('mouseleave', () :void => {
                callback(settings.out, this);
            });
            break;

        case 'toggle':
            this._toggle = false;
            if (touch) {
                this.addEventListener('touchend', (e :TouchEvent) :void => {
                    e.stopPropagation();
                    if (!this._toggle) {
                        e.preventDefault();
                        this._toggle = true;
                        callback(settings.in, this);
                        return;
                    }
                    this._toggle = false;
                    callback(settings.out, this);
                });
                window.addEventListener('touchend', (e :TouchEvent) :void => {
                    if (e.target?.closest('a[href]')) {
                        return;
                    }
                    if (this._toggle) {
                        callback(settings.out, this);
                        this._toggle = false;
                    }
                });
                return;
            }
            this.addEventListener('click', (e :PointerEvent) :void => {
                if (!this._toggle) {
                    e.preventDefault();
                    this._toggle = true;
                    callback(settings.in, this);
                    return;
                }
                this._toggle = false;
                callback(settings.out, this);
            })
            break;
    }
};

/**
 * Toggle an element.
 */
HTMLElement.prototype.toggle = function(options :object) :void
{
    options.mode = 'toggle';
    this.hover(options);
};

/**
 * Hover on multiple elements within a NodeList.
 */
NodeList.prototype.hover = function(options :object) :void
{
    this.forEach((element :Node) :void => {
        element.hover(options);
    });
};

/**
 * Toggle multiple elements within a NodeList.
 */
NodeList.prototype.toggle = function(options :object) :void
{
    this.forEach((element :Node) :void => {
        element.toggle(options);
    });
};